import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { completedDocRequests, docRequestStatus } from '../CaseReview/SummaryCaseTable/components/DocRequestChip.js';
import { isStipFinalizedCase } from '../CaseReview/SummaryCaseTable/services/summaryRowCreationService.js';
import { usePersistedDataStore } from '../store.js';

const TOWN_RESPONSE_ACTION_BUTTON_SX = { minWidth: 76, px: 1 };

/** Prefer BAR settle date when present; otherwise SCAR (town cancel eligibility). */
function effectiveSettleDateRaw(row) {
  if (row?.BARSettleDate != null && row.BARSettleDate !== '') return row.BARSettleDate;
  return row?.SCARSettleDate ?? null;
}

/** True if settle date is today or up to `maxCalendarDays` calendar days in the past. */
function isSettleDateWithinRecentCancelWindow(row, maxCalendarDays = 2) {
  const raw = effectiveSettleDateRaw(row);
  if (raw == null || raw === '') return false;
  const t = new Date(raw);
  if (Number.isNaN(t.getTime())) return false;
  const settleDay = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = (today - settleDay) / 864e5;
  return diffDays >= 0 && diffDays <= maxCalendarDays;
}

const pendingChipSx = {
  bgcolor: 'grey.300',
  color: 'text.secondary',
  fontWeight: 600,
  '& .MuiChip-icon': { color: 'text.secondary !important' },
  '& .MuiChip-label': { color: 'text.secondary' },
};

const settledFilledChipSx = {
  bgcolor: 'var(--green-sweep)',
  color: '#fff',
  fontWeight: 600,
  '& .MuiChip-icon': { color: '#fff !important' },
  '& .MuiChip-label': { color: '#fff' },
};

const TOWN_ISSUE_LABELS = {
  SoleAuth: 'Multiple Grievance Filings',
  ProofOccupancy: 'I believe the Homeowner does not live at the property',
  ProofLLC: 'Corporate Entity (Trust / LLC) Owner',
};

const issueLabel = (req) => TOWN_ISSUE_LABELS[req?.docType] || req?.docType || 'Issue';

export const TownIssueMarks = ({ requests }) => {
  const open = docRequestStatus(requests)?.open || [];
  const completed = completedDocRequests(requests);
  if (!open.length && !completed.length) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
      {open.length > 0 && (
        <Tooltip
          title={(
            <div>
              <div>{`You have flagged ${open.length === 1 ? 'an issue' : 'issues'}`}</div>
              {open.map((req, index) => <div key={req.requestId ?? index}>{issueLabel(req)}</div>)}
            </div>
          )}
          placement="top"
          disableInteractive
        >
          <ReportProblemOutlinedIcon aria-label="Flagged issue" sx={{ fontSize: 19, color: 'warning.main', display: 'block' }} />
        </Tooltip>
      )}
      {completed.length > 0 && (
        <Tooltip
          title={(
            <div>
              {completed.map((req, index) => <div key={req.requestId ?? index}>{issueLabel(req)}</div>)}
              <div>Supporting documentation has been uploaded to NYSCEF</div>
            </div>
          )}
          placement="top"
          disableInteractive
        >
          <CloudDoneOutlinedIcon aria-label="Supporting documentation has been uploaded to NYSCEF" sx={{ fontSize: 19, color: 'var(--green-sweep)', display: 'block' }} />
        </Tooltip>
      )}
    </Box>
  );
};

const countersignedOutlinedChipSx = {
  bgcolor: 'transparent',
  color: 'var(--green-text)',
  fontWeight: 600,
  borderColor: 'var(--green-sweep)',
  '& .MuiChip-icon': { color: 'var(--green-sweep) !important' },
  '& .MuiChip-label': { color: 'var(--green-text)' },
};

/**
 * Town Response column for town portal: Accept + Counter or Offer vs Settled chip.
 * In-flight accept/cancel uses gray status chips so the grid row model need not update early.
 */
const TownResponseCell = ({
  row,
  townAcceptParcelId,
  focusTownOfferCell,
  onTownAcceptOffer,
  onTownCancelSettlement,
}) => {
  const [cancelInFlight, setCancelInFlight] = useState(false);
  const stipsDownloadInFlight = usePersistedDataStore((state) => state.stipsDownloadInFlight);

  if (row.loading) {
    return <span style={{ color: '#888', fontStyle: 'italic' }}>Loading...</span>;
  }

  const acceptBusy = townAcceptParcelId != null && townAcceptParcelId === row.parcel_id;
  const acceptDisabledNoOffersSent = !!row._townWorkupNotAvailable;
  const townOfferAsPrimaryAction = !!row._townAventineDisplayNotAvailable;
  const townOfferActionLabel = townOfferAsPrimaryAction ? 'Offer' : 'Counter';
  const stipFinalized = row.StipFinalized || isStipFinalizedCase(row);
  const showSettledChip = row._isSettled || stipFinalized;
  const settledChipLabel = stipFinalized
    ? 'Countersigned'
    : (cancelInFlight ? 'Cancelling...' : 'Settled');

  if (row.AwaitingDecision && !showSettledChip) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Tooltip title="Awaiting decision" placement="top" disableInteractive>
          <Chip
            size="small"
            icon={<HourglassEmptyIcon sx={{ color: 'inherit !important' }} />}
            label="Awaiting Decision"
            sx={pendingChipSx}
          />
        </Tooltip>
      </Box>
    );
  }

  if (acceptBusy && !showSettledChip) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Chip
          size="small"
          icon={<HourglassEmptyIcon sx={{ color: 'inherit !important' }} />}
          label="Settling..."
          sx={pendingChipSx}
        />
      </Box>
    );
  }

  const showCancelSettlement =
    row._isSettled &&
    !stipFinalized &&
    onTownCancelSettlement &&
    isSettleDateWithinRecentCancelWindow(row);
  const cancelBlockedByStipsDownload = !!stipsDownloadInFlight;

  if (showSettledChip && !row.AwaitingDecision) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 0.5 }}>
        <Chip
          size="small"
          icon={
            cancelInFlight ? (
              <HourglassEmptyIcon sx={{ color: 'inherit !important' }} />
            ) : (
              <CheckCircleIcon
                sx={{
                  color: stipFinalized ? 'var(--green-sweep) !important' : '#fff !important',
                }}
              />
            )
          }
          label={settledChipLabel}
          sx={
            cancelInFlight
              ? pendingChipSx
              : stipFinalized
                ? countersignedOutlinedChipSx
                : settledFilledChipSx
          }
          variant={stipFinalized ? 'outlined' : 'filled'}
        />
        {showCancelSettlement && !cancelInFlight && (
          <Tooltip
            title={cancelBlockedByStipsDownload ? 'Wait for stipulations to finish downloading' : 'Cancel settlement'}
            placement="top"
            disableInteractive
          >
            <span>
              <IconButton
                size="small"
                disabled={cancelInFlight || cancelBlockedByStipsDownload}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (cancelInFlight || cancelBlockedByStipsDownload || !onTownCancelSettlement) return;
                  setCancelInFlight(true);
                  try {
                    await onTownCancelSettlement(row);
                  } finally {
                    setCancelInFlight(false);
                  }
                }}
                sx={{
                  color: 'error.main',
                  p: 0.25,
                  '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.08)' },
                }}
                aria-label="Cancel settlement"
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      <Button
        size="small"
        variant="contained"
        color="success"
        disabled={acceptBusy || acceptDisabledNoOffersSent}
        onClick={(e) => {
          e.stopPropagation();
          if (acceptBusy || acceptDisabledNoOffersSent) return;
          onTownAcceptOffer?.(row);
        }}
        sx={TOWN_RESPONSE_ACTION_BUTTON_SX}
      >
        Accept
      </Button>
      <Button
        size="small"
        variant={townOfferAsPrimaryAction ? 'contained' : 'outlined'}
        color="primary"
        onClick={(e) => {
          e.stopPropagation();
          focusTownOfferCell?.(row);
        }}
        sx={TOWN_RESPONSE_ACTION_BUTTON_SX}
      >
        {townOfferActionLabel}
      </Button>
    </Box>
  );
};

export default TownResponseCell;
